import React, { useRef, useEffect } from 'react';
import './Home.css';

const DepthField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    let rafId, w, h;

    const orbs = [
      // Focal — dominant light source behind the hero name
      { bx:0.38, by:0.38, sx: 80, sy: 60, fpx:0.018, fpy:0.013, px:0.0, py:0.0, r:520, peak:0.21, color:'23, 126, 137' },
      // Atmosphere — wide, dim, peripheral teal
      { bx:0.85, by:0.15, sx:160, sy:100, fpx:0.022, fpy:0.016, px:2.1, py:1.4, r:780, peak:0.05, color:'23, 126, 137' },
      { bx:0.10, by:0.78, sx:140, sy:120, fpx:0.019, fpy:0.022, px:4.5, py:3.2, r:820, peak:0.05, color:'23, 126, 137' },
      { bx:0.08, by:0.12, sx:130, sy: 90, fpx:0.017, fpy:0.021, px:3.3, py:5.7, r:760, peak:0.04, color:'23, 126, 137' },
      // Colour temperature — violet gives the dark field a cool depth
      { bx:0.75, by:0.72, sx:120, sy:130, fpx:0.024, fpy:0.018, px:1.6, py:4.8, r:740, peak:0.065, color:'95, 35, 200' },
    ];

    const init = () => {
      const dpr = window.devicePixelRatio || 1;
      w = canvas.offsetWidth  || canvas.parentElement?.offsetWidth  || window.innerWidth;
      h = canvas.offsetHeight || canvas.parentElement?.offsetHeight || window.innerHeight;
      canvas.width  = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const tick = ts => {
      const t = ts * 0.001;

      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();

      orbs.forEach(o => {
        const x = o.bx * w + Math.sin(t * o.fpx + o.px) * o.sx;
        const y = o.by * h + Math.sin(t * o.fpy + o.py) * o.sy;

        const g = ctx.createRadialGradient(x, y, 0, x, y, o.r);
        g.addColorStop(0,    `rgba(${o.color}, ${o.peak})`);
        g.addColorStop(0.38, `rgba(${o.color}, ${(o.peak * 0.5).toFixed(3)})`);
        g.addColorStop(0.72, `rgba(${o.color}, ${(o.peak * 0.12).toFixed(3)})`);
        g.addColorStop(1,    `rgba(${o.color}, 0)`);

        ctx.beginPath();
        ctx.arc(x, y, o.r, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      });

      rafId = requestAnimationFrame(tick);
    };

    init();
    rafId = requestAnimationFrame(tick);
    window.addEventListener('resize', init);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', init);
    };
  }, []);

  return <canvas ref={canvasRef} className="matrix-canvas" />;
};


const Home = () => (
  <section id="home" className="section visible">
    <DepthField />

    <div className="hero-content">

      {/* ── Left column ── */}
      <div className="hero-left">
        <div className="hero-identity">
          <span className="hero-identity-builder">Builder.</span>
          <span className="hero-identity-architect">Architect.</span>
          <span className="hero-identity-engineer">Engineer.</span>
        </div>

        <p className="hero-tagline">
          The one who doesn't leave until it ships.
        </p>

        <p className="hero-bio">
          ML pipelines, API gateways, semantic search engines —
          built for real load, not demos.
          From the whiteboard to production infrastructure.
        </p>

        <div className="hero-stats">
          <span className="hero-stat">6 systems in production</span>
          <span className="hero-stat-sep">·</span>
          <span className="hero-stat">GCP</span>
          <span className="hero-stat-sep">·</span>
          <span className="hero-stat">Go</span>
          <span className="hero-stat-sep">·</span>
          <span className="hero-stat">Python</span>
        </div>

        <div className="hero-ctas">
          <a href="#projects" className="cta-primary">
            See What I've Built ↓
          </a>
          <a
            href="/images/resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-secondary"
          >
            Resume ↗
          </a>
        </div>

        <p className="hero-status">
          <span className="hero-status-cursor">▸</span>
          {' '}new york · available for the right role
        </p>
      </div>

      {/* ── Right column ── */}
      <div className="hero-right">
        <img
          src="/images/headshot.png"
          alt="Adam Dsouza"
          className="hero-headshot"
        />
        <div className="hero-signal">
          <div className="hero-signal-header">
            <span className="hero-signal-name">sentinel-ml</span>
            <span className="hero-signal-live">● live</span>
          </div>
          <div className="hero-signal-divider" />
          <div className="hero-signal-output">
            <span className="hero-signal-prompt">{'>'} classifying market stream</span>
            <span className="hero-signal-classes">
              <span className="hero-signal-bull">BULLISH</span>
              <span className="hero-signal-sep"> / </span>
              <span className="hero-signal-bear">BEARISH</span>
              <span className="hero-signal-sep"> / </span>
              <span className="hero-signal-neutral">NEUTRAL</span>
            </span>
          </div>
        </div>
      </div>

    </div>
  </section>
);

export default Home;
